import { z } from 'zod';

import { timestamp } from '~/services/types.ts';

function normalizeComponentUnit(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const normalized = value.toLowerCase();
  if (normalized === 'ppmmol') {
    return 'PpmMol';
  }
  if (normalized === 'micrometer') {
    return 'Micrometer';
  }
  if (normalized === 'ppmww') {
    return 'PpmWw';
  }

  return value;
}

function normalizeComponentInputType(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const normalized = value.toLowerCase();
  if (normalized === 'numeric') {
    return 'Numeric';
  }
  if (normalized === 'boolean') {
    return 'Boolean';
  }

  return value;
}

function normalizeQualifier(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const normalized = value.toLowerCase();
  if (normalized === 'exact') {
    return 'Exact';
  }
  if (normalized === 'notdetected') {
    return 'NotDetected';
  }
  if (normalized === 'detected') {
    return 'Detected';
  }

  return value;
}

export const componentUnitSchema = z.preprocess(
  normalizeComponentUnit,
  z.enum(['PpmMol', 'Micrometer', 'PpmWw']),
);
export type ComponentUnit = z.infer<typeof componentUnitSchema>;

export const componentInputTypeSchema = z.preprocess(
  normalizeComponentInputType,
  z.enum(['Numeric', 'Boolean']),
);
export type ComponentInputType = z.infer<typeof componentInputTypeSchema>;

export const declarationStatusEnum = z.enum(['Draft', 'Declared', 'Superseded']);
export type DeclarationStatus = z.infer<typeof declarationStatusEnum>;

export const componentQualifierSchema = z.preprocess(
  normalizeQualifier,
  z.enum(['Exact', 'NotDetected', 'Detected']),
);
export type ComponentQualifier = z.infer<typeof componentQualifierSchema>;

export const componentSchema = z.object({
  id: z.string(),
  key: z.string(),
  code: z.string(),
  name: z.string(),
  unit: componentUnitSchema,
  inputType: componentInputTypeSchema,
});
export type Component = z.infer<typeof componentSchema>;

export const standardItemSchema = z.object({
  id: z.string(),
  standardVersionId: z.string(),
  componentId: z.string(),
  displayOrder: z.number(),
  maxValue: z.number().nullable(),
  detectionLimit: z.number().nullable(),
  quantificationLimit: z.number().nullable(),
});
export type StandardItem = z.infer<typeof standardItemSchema>;

export const compositionStandardSchema = z.object({
  id: z.string(),
  version: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  items: standardItemSchema.array(),
});
export type CompositionStandard = z.infer<typeof compositionStandardSchema>;

export const declarationItemSchema = z.object({
  id: z.string(),
  declarationId: z.string(),
  schemaItemId: z.string(),
  componentId: z.string(),
  qualifier: componentQualifierSchema,
  value: z.number().nullable(),
  note: z.string().nullable().default(null),
  basisItemId: z.string().nullable(),
});
export type DeclarationItem = z.infer<typeof declarationItemSchema>;

export type ComponentValueUpdate = {
  componentKey: string;
  value: number | null;
  qualifier?: ComponentQualifier | null;
  note?: string | null;
};

export const componentValidationResultSchema = z.object({
  componentId: z.string(),
  componentKey: z.string(),
  componentName: z.string(),
  isValid: z.boolean(),
  isWithinSpec: z.boolean(),
  isBasisBacked: z.boolean().default(false),
  isMissing: z.boolean(),
  value: z.number().nullable(),
  maxValue: z.number().nullable(),
  detectionLimit: z.number().nullable(),
  quantificationLimit: z.number().nullable().default(null),
  message: z.string().nullable(),
});
export type ComponentValidationResult = z.infer<typeof componentValidationResultSchema>;

export const validationResponseSchema = z.object({
  isValid: z.boolean(),
  isWithinSpec: z.boolean(),
  errors: z.string().array(),
  warnings: z.string().array(),
  componentResults: componentValidationResultSchema.array(),
});
export type ValidationResponse = z.infer<typeof validationResponseSchema>;

export const declarationSchema = z.object({
  id: z.string(),
  cargoId: z.string().nullable(),
  batchId: z.string().nullable(),
  projectId: z.string(),
  schemaId: z.string(),
  status: declarationStatusEnum,
  isPreliminary: z.boolean().default(false),
  isWithinSpec: z.boolean().nullable().default(null),
  createdByUserId: z.string().nullable(),
  createdAt: timestamp,
  declaredAt: timestamp.nullable(),
  declaredByName: z.string().nullable().default(null),
  projectName: z.string().nullable().default(null),
  generalNote: z.string().nullable().default(null),
  items: declarationItemSchema.array(),
});
export type Declaration = z.infer<typeof declarationSchema>;

export const componentFieldSchema = z.object({
  declarationItemId: z.string(),
  value: z.number().nullable(),
  qualifier: componentQualifierSchema.default('Exact'),
  note: z.string().nullable().default(null),
  basisItemId: z.string().nullable(),
  basisSummary: z.string().nullable().default(null),
  basisDocumentReference: z.string().nullable().default(null),
  basisId: z.string().nullable().default(null),
  componentId: z.string(),
  componentKey: z.string(),
  componentCode: z.string(),
  componentName: z.string(),
  unit: componentUnitSchema,
  inputType: componentInputTypeSchema,
  maxValue: z.number().nullable(),
  detectionLimit: z.number().nullable(),
  quantificationLimit: z.number().nullable().default(null),
  displayOrder: z.number(),
  isBasisBacked: z.boolean().default(false),
});
export type ComponentField = z.infer<typeof componentFieldSchema>;

export const declarationFormSchema = z.object({
  id: z.string(),
  cargoId: z.string().nullable(),
  batchId: z.string().nullable(),
  projectId: z.string(),
  status: declarationStatusEnum,
  createdAt: timestamp,
  declaredAt: timestamp.nullable(),
  isPreliminary: z.boolean().default(false),
  estimatedCo2Purity: z.number().nullable().default(null),
  fields: componentFieldSchema.array(),
  declaredByName: z.string().nullable().default(null),
  projectName: z.string().nullable().default(null),
  generalNote: z.string().nullable().default(null),
  voyageReferenceNo: z.string().nullish().transform(v => v ?? ''),
  legalAcknowledgementText: z.string().min(1),
});
export type DeclarationForm = z.infer<typeof declarationFormSchema>;

export const cargoStatusEnum = z.enum(['Pending', 'InProgress', 'Completed']);
export type CargoStatus = z.infer<typeof cargoStatusEnum>;

export const compositionStatusEnum = z.enum(['None', 'Draft', 'Declared']);
export type CompositionStatus = z.infer<typeof compositionStatusEnum>;

export const cargoCompositionStatusSchema = z.object({
  cargoId: z.string(),
  projectId: z.string(),
  createdAt: timestamp,
  cargoStatus: cargoStatusEnum,
  committedQuantityKilograms: z.number(),
  receivedQuantityKilograms: z.number(),
  voyageReferenceNo: z.string().nullish().transform(v => v ?? ''),
  compositionStatus: compositionStatusEnum,
  currentDeclarationId: z.string().nullable(),
  declaredAt: timestamp.nullable(),
  currentDeclarationIsPreliminary: z.boolean().nullable().default(null),
  isWithinSpec: z.boolean().nullable().default(null),
});
export type CargoCompositionStatus = z.infer<typeof cargoCompositionStatusSchema>;
